import { useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { debounce } from 'lodash';
import { useEffect } from 'react';

import { ClientRequestFirestoreResponse, ClientRequestsDataModel } from '../../types';
import { fetchPaginatedCollectionRealtime } from '../utils';

const COLLECTION_PATH = 'client-request';

export const useGetClientRequestsFromFirestore = (
  elements: number,
  page: number,
  options?: UseQueryOptions<ClientRequestsDataModel, FirebaseError, ClientRequestsDataModel>,
): UseQueryResult<ClientRequestsDataModel, FirebaseError> => {
  const queryClient = useQueryClient();
  const subscriptionKey = [COLLECTION_PATH, page, elements];

  useEffect(() => {
    const updateData = debounce(
      (data: ClientRequestFirestoreResponse[], totalPages: number) => {
        queryClient.setQueryData<ClientRequestsDataModel>(subscriptionKey, {
          data,
          totalPages,
          isLoading: false,
          error: null,
        });
      },
      80,
    );

    const unsubscribePromise = fetchPaginatedCollectionRealtime<ClientRequestFirestoreResponse>(
      COLLECTION_PATH,
      page,
      elements,
      'createdAt',
      (data, totalPages) => updateData(data, totalPages),
      (error) => {
        queryClient.setQueryData<ClientRequestsDataModel>(subscriptionKey, {
          data: [],
          totalPages: 0,
          isLoading: false,
          error,
        });
      },
    );

    return () => {
      updateData.cancel();
      unsubscribePromise.then((unsubscribe) => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, elements, queryClient]);

  return useQuery<ClientRequestsDataModel, FirebaseError, ClientRequestsDataModel>({
    queryKey: subscriptionKey,
    queryFn: async () => {
      const cached = queryClient.getQueryData<ClientRequestsDataModel>(subscriptionKey);
      if (cached) return cached;

      return { data: [], totalPages: 0, isLoading: true, error: null };
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
};
